import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Invoices from './pages/Invoices'
import InvoiceDetail from './pages/InvoiceDetail'
import Orders from './pages/Orders'
import OrderDetail from './pages/OrderDetail'
import Shipments from './pages/Shipments'
import ShipmentDetail from './pages/ShipmentDetail'
import Tickets from './pages/Tickets'
import TicketDetail from './pages/TicketDetail'
import Catalog from './pages/Catalog'
import Cart from './pages/Cart'
import Proposals from './pages/Proposals'
import Profile from './pages/Profile'
import ProposalDetail from './pages/ProposalDetail'
import Projects from './pages/Projects'
import Register from './pages/Register'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminERPs from './pages/admin/AdminERPs'
import AdminERPForm from './pages/admin/AdminERPForm'
import AdminCustomers from './pages/admin/AdminCustomers'
import AdminUsers from './pages/admin/AdminUsers'
import AdminActivityLogs from './pages/admin/AdminActivityLogs'
import AdminRegistrationRequests from './pages/admin/AdminRegistrationRequests'
import AdminGroups from './pages/admin/AdminGroups'
import AdminPendingOrders from './pages/admin/AdminPendingOrders'
import AdminProposals from './pages/admin/AdminProposals'
import AdminDocumentSignatures from './pages/admin/AdminDocumentSignatures'
import AdminRevisions from './pages/admin/AdminRevisions'
import AdminProducts from './pages/admin/AdminProducts'
import AdminUsage from './pages/admin/AdminUsage'
import AdminBilling from './pages/admin/AdminBilling'
import AdminMigration from './pages/admin/AdminMigration'
import AdminHelpTooltips from './pages/admin/AdminHelpTooltips'
import AdminProfile from './pages/admin/AdminProfile'
import AdminNotificationRules from './pages/admin/AdminNotificationRules'
import AcceptInvitation from './pages/AcceptInvitation'
import MentionsLegales from './pages/legal/MentionsLegales'
import Confidentialite from './pages/legal/Confidentialite'
import CGU from './pages/legal/CGU'
import CGV from './pages/legal/CGV'
import Tarifs from './pages/Tarifs'
import Subscribe from './pages/Subscribe'
import MyPayments from './pages/MyPayments'
import MesCredits from './pages/MesCredits'
import Revisions from './pages/Revisions'
import DocumentSignaturesPage from './pages/DocumentSignaturesPage'
import MyTeam from './pages/MyTeam'
import Gallery from './pages/Gallery'
import LandingPage from './pages/LandingPage'
import PublicShop from './pages/PublicShop'
import Layout from './components/Layout'
import { CartProvider } from './contexts/CartContext'
import { DarkModeProvider } from './contexts/DarkModeContext'
import { ModulesProvider } from './contexts/ModulesContext'
import { useModules } from './hooks/useModules'
import { Outlet } from 'react-router-dom'

// Layout unique pour toutes les routes authentifiées
function PrivateLayout() {
  const user = localStorage.getItem('user')
  const token = localStorage.getItem('auth_token')
  if (!user || !token) {
    return <Navigate to="/login" />
  }
  return <Layout />
}

// Guard module — redirects to /dashboard if module is disabled for this ERP.
// Returns null while modules are loading to avoid a premature redirect.
function ModuleGuard({ module }: { module: string }) {
  const { enabledModules, modulesLoaded } = useModules()
  if (!modulesLoaded) return null
  if (!enabledModules[module]) return <Navigate to="/dashboard" replace />
  return <Outlet />
}

// Guard admin sans Layout (le Layout est déjà monté par PrivateLayout)
function AdminGuard() {
  const userStr = localStorage.getItem('user')
  if (!userStr) return <Navigate to="/login" />
  try {
    const user = JSON.parse(userStr)
    if (!['admin', 'super_admin'].includes(user.role)) return <Navigate to="/" />
  } catch {
    return <Navigate to="/login" />
  }
  return <Outlet />
}

/**
 * Composant de redirection pour la catégorie par défaut
 */
const ShopRedirect = () => {
  const { siren, productId } = useParams()
  return <Navigate replace to={`/shop/${siren}/all/${productId}`} />
}

function App() {
  return (
    <BrowserRouter
      basename={import.meta.env.BASE_URL}
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <DarkModeProvider>
        <ModulesProvider>
          <CartProvider>
            <Routes>
              {/* Routes publiques */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/:siren" element={<Login />} />
              <Route path="/shop/:siren" element={<PublicShop />} />
              <Route path="/shop/:siren/:categoryLabel" element={<PublicShop />} />

              {/* Route avec catégorie (Route principale) */}
              <Route path="/shop/:siren/:categoryLabel/:productId" element={<PublicShop />} />

              {/* Redirection vers catégorie par défaut si l'URL est /shop/:siren/product/:id */}
              <Route path="/shop/:siren/product/:productId" element={<ShopRedirect />} />

              <Route path="/register" element={<Register />} />
              <Route path="/subscribe" element={<Subscribe />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/accept-invitation" element={<AcceptInvitation />} />

              {/* Pages légales — publiques par nature */}
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/confidentialite" element={<Confidentialite />} />
              <Route path="/cgu" element={<CGU />} />
              <Route path="/cgv" element={<CGV />} />

              {/* Toutes les routes authentifiées — un seul Layout monté en permanence */}
              <Route element={<PrivateLayout />}>
                {/* Routes utilisateur */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route element={<ModuleGuard module="invoices" />}>
                  <Route path="/invoices" element={<Invoices />} />
                  <Route path="/invoices/:id" element={<InvoiceDetail />} />
                </Route>
                <Route element={<ModuleGuard module="orders" />}>
                  <Route path="/orders" element={<Orders />} />
                  <Route path="/orders/:id" element={<OrderDetail />} />
                </Route>
                <Route element={<ModuleGuard module="shipments" />}>
                  <Route path="/shipments" element={<Shipments />} />
                  <Route path="/shipments/:id" element={<ShipmentDetail />} />
                </Route>
                <Route element={<ModuleGuard module="tickets" />}>
                  <Route path="/tickets" element={<Tickets />} />
                  <Route path="/tickets/:id" element={<TicketDetail />} />
                </Route>
                <Route element={<ModuleGuard module="catalog" />}>
                  <Route path="/catalog" element={<Catalog />} />
                  <Route path="/cart" element={<Cart />} />
                </Route>
                <Route element={<ModuleGuard module="proposals" />}>
                  <Route path="/proposals" element={<Proposals />} />
                  <Route path="/proposals/:id" element={<ProposalDetail />} />
                </Route>
                <Route element={<ModuleGuard module="projects" />}>
                  <Route path="/projects" element={<Projects />} />
                </Route>
                <Route element={<ModuleGuard module="gallery" />}>
                  <Route path="/gallery" element={<Gallery />} />
                </Route>
                <Route element={<ModuleGuard module="revisions" />}>
                  <Route path="/revisions" element={<Revisions />} />
                </Route>
                <Route element={<ModuleGuard module="document_signatures" />}>
                  <Route path="/document-signatures" element={<DocumentSignaturesPage />} />
                </Route>
                <Route path="/payments" element={<MyPayments />} />
                <Route path="/mes-credits" element={<MesCredits />} />
                <Route path="/my-team" element={<MyTeam />} />
                <Route path="/tarifs" element={<Tarifs />} />
                <Route path="/profile" element={<Profile />} />

                {/* Routes admin — guard seul, pas de nouveau Layout */}
                <Route element={<AdminGuard />}>
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/erps" element={<AdminERPs />} />
                  <Route path="/admin/erps/new" element={<AdminERPForm />} />
                  <Route path="/admin/erps/:id" element={<AdminERPForm />} />
                  <Route path="/admin/customers" element={<AdminCustomers />} />
                  <Route path="/admin/users" element={<AdminUsers />} />
                  <Route path="/admin/logs" element={<AdminActivityLogs />} />
                  <Route path="/admin/registrations" element={<AdminRegistrationRequests />} />
                  <Route path="/admin/groups" element={<AdminGroups />} />
                  <Route path="/admin/pending-orders" element={<AdminPendingOrders />} />
                  <Route path="/admin/proposals" element={<AdminProposals />} />
                  <Route path="/admin/document-signatures" element={<AdminDocumentSignatures />} />
                  <Route path="/admin/revisions" element={<AdminRevisions />} />
                  <Route path="/admin/products" element={<AdminProducts />} />
                  <Route path="/admin/usage" element={<AdminUsage />} />
                  <Route path="/admin/billing" element={<AdminBilling />} />
                  <Route path="/admin/migration" element={<AdminMigration />} />
                  <Route path="/admin/help-tooltips" element={<AdminHelpTooltips />} />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                  <Route path="/admin/notification-rules" element={<AdminNotificationRules />} />
                </Route>
              </Route>
            </Routes>
          </CartProvider>
        </ModulesProvider>
      </DarkModeProvider>
    </BrowserRouter>
  )
}

export default App
